import React, { useMemo } from "react";

// Material UI
import { Stack, Box, Typography, Alert } from "@mui/material";

// Styles
import { commonStyles, textStyles } from "Common/Styles";

// Components
import {
  AddNewIcon,
  CardInfo,
  FormikTextField,
  AdminSettingDeleteHeader,
} from "Common/Components";
import { RadioButtonsPreview } from "../../Common";

// Fields
import { reasonsFields } from "../Common/fields";

// Translation
import { useTranslation } from "react-i18next";

// Utils
import { sortReasons } from "Utils/Reasons";

const ReasonsPage = ({ formik, viewOnly = false }) => {
  const { t } = useTranslation();
  const handleAddReason = () => {
    const newReasons = [
      ...(formik.values.appointmentReasons || []),
      {
        en: "",
        fr: "",
      },
    ];
    formik.setFieldValue("appointmentReasons", newReasons);
  };

  const handleRemoveReason = (index) => {
    const newReasons = [...formik.values.appointmentReasons];
    newReasons.splice(index, 1);
    formik.setFieldValue("appointmentReasons", newReasons);
  };

  const reasonsEnSorted = useMemo(
    () =>
      sortReasons({
        reasons: formik.values.appointmentReasons,
        words:
          t("highlightWords.sorting", { lng: "en", returnObjects: true }) || [],
        getValue: (r) => r?.en,
      }),
    [formik.values.appointmentReasons, t]
  );

  const reasonsFrSorted = useMemo(
    () =>
      sortReasons({
        reasons: formik.values.appointmentReasons,
        words:
          t("highlightWords.sorting", { lng: "fr", returnObjects: true }) || [],
        getValue: (r) => r?.fr,
      }),
    [formik.values.appointmentReasons, t]
  );

  return (
    <>
      <CardInfo
        title="Reasons for Visit"
        tooltipInfo="Patients will see these reasons as a radio buttons options during booking."
        addButtonTitle={!viewOnly && "Add Reason"}
        addButtonOnClick={handleAddReason}
      >
        {formik.touched.appointmentReasons &&
          typeof formik.errors.appointmentReasons === "string" && (
            <Alert severity="error">{formik.errors.appointmentReasons}</Alert>
          )}
        {!viewOnly &&
          (formik.values.appointmentReasons || []).map((_, index) => (
            <Box key={index} sx={commonStyles.fieldArrayRow}>
              <AdminSettingDeleteHeader
                name="Reason"
                index={index}
                onDeleteClick={() => handleRemoveReason(index)}
              />
              <Stack direction="row" alignItems="flex-start" gap={2}>
                {!viewOnly &&
                  reasonsFields.map((field) => (
                    <FormikTextField
                      key={`${field.language}-${index}`}
                      formik={formik}
                      field={field}
                      name={`appointmentReasons.${index}.${field.language}`}
                    />
                  ))}
              </Stack>
            </Box>
          ))}
        {!viewOnly && formik.values.appointmentReasons.length > 0 && (
          <AddNewIcon
            onClick={handleAddReason}
            tooltipInfo="Add a new reason"
          />
        )}
        {(formik.values.appointmentReasons || []).length > 0 && (
          <Box>
            <Typography sx={textStyles.subtitle2}>Preview</Typography>
            <Stack direction="row" alignItems="flex-start" gap={2}>
              <RadioButtonsPreview
                reasons={reasonsEnSorted}
                language="en"
                label="English"
              />
              <RadioButtonsPreview
                reasons={reasonsFrSorted}
                language="fr"
                label="French"
              />
            </Stack>
          </Box>
        )}
      </CardInfo>
    </>
  );
};

export default ReasonsPage;
